import React, { useState, useEffect } from "react";
import { stringify, parse } from "svgson";
import { UploadOutlined } from '@ant-design/icons';
import { Slider, InputNumber, Button, Upload } from "antd";
import { t } from "./i18n";



//Create a functional component named SVGtoJPEGResizer
function SVGtoJPEGResizer() {
  // Define state variables using the useState hook
  const [file, setFile] = useState(null); // SVG file uploaded to be resized
  const [width, setWidth] = useState(0); // Width of the resulting JPEG image
  const [height, setHeight] = useState(0); // Height of the resulting JPEG image
  const [aspectRatio, setAspectRatio] = useState(null); // Aspect ratio of the SVG file
  const [sliderDisabled, setSliderDisabled] = useState(true); // Whether the slider is disabled or not
  const [inputWidth, setInputWidth] = useState(width); // Width entered in the input field
  const [inputHeight, setInputHeight] = useState(height); // Height entered in the input field
  const defaultWidth = 1500; // Default width of the resulting JPEG image
  

   // Function to handle the file upload
  const handleFileUpload = () => {
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = async () => {
      const svgText = reader.result;
      const svgJson = await parse(svgText);
      console.log(svgJson);
      const canvas = document.createElement("canvas");

      const svgWidth = svgJson.attributes.viewBox.split(" ")[2];
      const svgHeight = svgJson.attributes.viewBox.split(" ")[3];
      const aspectRatio = svgHeight / svgWidth;
      const newHeight = width * aspectRatio;
      setHeight(newHeight);

      canvas.width = width;
      canvas.height = newHeight;

      const svgToSave = await stringify(svgJson);
      const img = new Image();
      img.src = "data:image/svg+xml;base64," + btoa(svgToSave);
      img.onload = function () {
        const ctx = canvas.getContext("2d");
        // make the background white
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataURL = canvas.toDataURL("image/jpeg");

        const link = document.createElement("a");
        link.download = "image.jpg";
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
    };
  };

  // Function to update the aspect ratio state variable
  const updateAspectRatio = (svgJson) => {
    const svgWidth = svgJson.attributes.viewBox.split(" ")[2];
    const svgHeight = svgJson.attributes.viewBox.split(" ")[3];
    const newAspectRatio = svgHeight / svgWidth;
    setAspectRatio(newAspectRatio);
    setSliderDisabled(false);
    setInputWidth(defaultWidth);
    setInputHeight(Math.round(defaultWidth * newAspectRatio));
  };
  
// Use the useEffect hook to handle changes in file or defaultWidth state variables
  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = async () => {
        const svgText = reader.result;
        const svgJson = await parse(svgText);
        updateAspectRatio(svgJson);
      };
    } else {
      setWidth(defaultWidth);
      setHeight(aspectRatio ? Math.round(defaultWidth * aspectRatio) : defaultWidth);
      setInputWidth(defaultWidth);
      setInputHeight(aspectRatio ? Math.round(defaultWidth * aspectRatio) : defaultWidth);
      setSliderDisabled(true);
    }
  }, [file, defaultWidth, aspectRatio]);
  


  useEffect(() => {
    if (aspectRatio) {
      setHeight(width * aspectRatio);
    }
  }, [aspectRatio, width]);

  // Function to handle changes in the slider
  const handleSliderChange = (value) => {
    setWidth(value); // Set the width to the new slider value
    setHeight(value * aspectRatio);
    setInputWidth(value); // Set the width to the new slider value
    setInputHeight(value * aspectRatio);
  };


// Function to handle changes in the input number fields, adjusts other input number field accordingly
  const handleInputNumberChange = (value, inputType) => {
    if (inputType === "width") { // If the width input is changed set the width to the new value
      setInputWidth(value);
      setWidth(value);
      setHeight(value * aspectRatio);
      setInputHeight(value * aspectRatio);
    } else if (inputType === "height") { // If the height input is changed set the height to the new value
      setInputHeight(value);
      setHeight(value);
      setWidth(value / aspectRatio);
      setInputWidth(value / aspectRatio);
    }
  };

  // Object containing props for the Upload component
  const props = {
    beforeUpload: (file) => {
      setFile(file);
      return false; // Prevent the file from being uploaded automatically
    },
    // only 1 file
    multiple: false,  //allow only one file to be uploaded
    showUploadList: false, //hide the upload list
    // make upload list inline with button
    listType: "picture", //show the file as a picture thumbnail

    accept: ".svg", //only accept svg files
  };

  return (
    <div>
      <Upload {...props}> 
        <Button type="primary" icon={<UploadOutlined />}>{t("Choose File")}</Button>
        {file && <span style={{ marginLeft: "10px" }}>{file.name}</span>}
      </Upload>
      <div style={{ maxWidth: "500px", margin: "0 auto" }}>
        <Slider
          min={16}
          max={5000}
          step={1} //must be divisible by max-min values of slider
          value={width}
          onChange={handleSliderChange}

          tipFormatter={(value) => {
            if (!aspectRatio) {
              return t("Choose a file");
            }
            return `${value} x ${Math.round(value * aspectRatio)} px`;
          }}
          disabled={sliderDisabled}
        />

        <InputNumber
          min={16}
          max={5000}
          step={1}
          value={inputWidth}
          onChange={(value) => handleInputNumberChange(value, "width")}
          formatter={value => `${Math.round(value)}`}
          onPressEnter={() => {
            setWidth(inputWidth);
            setHeight(inputWidth * aspectRatio);
          }}
          disabled={sliderDisabled}
        />
        <span>   x   </span>

        <InputNumber
          min={16}
          max={5000}
          step={1}
          value={inputHeight}
          onChange={(value) => handleInputNumberChange(value, "height")}
          formatter={value => `${Math.round(value)}`}
          onPressEnter={() => {
            setHeight(inputHeight);
            setWidth(inputHeight / aspectRatio);
          }}
          disabled={sliderDisabled}
        />
        <span>  px  </span>


        <div style={{ marginTop: "20px" }}>
          <Button disabled={sliderDisabled} onClick={handleFileUpload}>{t("Convert and Download")}</Button>
        </div>
      </div>
    </div>
  );
}

export default SVGtoJPEGResizer;  